<template>
  <section class="facility-home">
    <!-- ✅ Fixed Navbar -->
    <MainNavbar class="navbar-fixed" />

    <!-- 🎬 Hero image + intro copy -->
    <div class="hero-section">
      <div class="video-wrapper">
        <img
          :src="heroImage"
          alt="Sports facilities across Kenya on IpromiseAfrica"
          class="hero-image"
          width="2172"
          height="724"
          decoding="async"
        />
      </div>

      <div class="hero-container">
        <div class="hero-content">
          <div class="tagline">
            <span class="tag">
              <i class="fas fa-building"></i>
              IpromiseAfrica Sports Facilities
            </span>
          </div>
          <h1 class="hero-title">
            Find & Book
            <span class="highlight">Great Facilities</span>
            Across Kenya
          </h1>
          <p class="hero-subtitle">
            Football pitches, basketball courts, volleyball courts, cricket grounds, and tennis courts —
            discover verified venues on IpromiseAfrica and book in seconds.
          </p>
          
          <div class="hero-actions">
            <button type="button" class="btn primary" @click="browseAllFacilities">
              <span>Find Facilities Near You</span>
              <svg class="btn-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z" />
              </svg>
            </button>
            <button type="button" class="btn outline" @click="$router.push('/ipromiseafrica/facility/owner-registration')">
              <span>List Your Facility</span>
              <svg class="btn-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
            </button>
          </div>

          <!-- Trust Indicators -->
          <div class="trust-indicators">
            <div class="trust-item">
              <div class="trust-icon">
                <i class="fas fa-shield-check"></i>
              </div>
              <div class="trust-text">Secure Bookings</div>
            </div>
            <div class="trust-item">
              <div class="trust-icon">
                <i class="fas fa-bolt"></i>
              </div>
              <div class="trust-text">Instant Confirmation</div>
            </div>
            <div class="trust-item">
              <div class="trust-icon">
                <i class="fas fa-star"></i>
              </div>
              <div class="trust-text">Verified Venues</div>
            </div>
          </div>
        </div>

        <div class="hero-footer">
          <div class="scroll-hint" @click="scrollToContent" role="button" tabindex="0" @keyup.enter="scrollToContent">
            <div class="scroll-text">Explore Sports</div>
            <div class="scroll-line">
              <div class="scroll-dot"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="section-wrapper" id="facility-sports">
      <div class="container">
        <div class="section-header">
          <div class="section-subtitle">IpromiseAfrica Facility Booking</div>
          <h2 class="section-title">Book by Sport</h2>
          <p class="section-description">
            Choose your sport and browse available venues — from community courts to premium grounds.
          </p>
        </div>

        <div class="sports-grid">
          <article
            v-for="sport in facilitySports"
            :key="sport.id"
            class="sport-card"
            @click="browseSport(sport)"
          >
            <div class="sport-card__media">
              <img
                :src="sport.image"
                :alt="`${sport.name} facility on IpromiseAfrica`"
                class="sport-card__img"
                loading="lazy"
                decoding="async"
              />
              <div class="sport-card__overlay" />
              <span class="sport-card__icon" aria-hidden="true">
                <i :class="sport.icon"></i>
              </span>
            </div>
            <div class="sport-card__body">
              <h3 class="sport-card__title">{{ sport.name }}</h3>
              <p class="sport-card__desc">{{ sport.description }}</p>
              <button type="button" class="sport-card__cta">
                Browse {{ sport.name }}
                <i class="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>

    <!-- 💡 Benefits Section -->
    <div class="benefits-wrapper">
      <div class="container">
        <div class="section-header">
          <div class="section-subtitle">Why Athletes Choose IpromiseAfrica</div>
          <h2 class="section-title">
            <span class="section-title__line">The Smart Way to</span>
            <span class="section-title__line">Book &amp; Play</span>
          </h2>
          <p class="section-description">
            From casual kickabouts to competitive matches — one platform for every sport facility you need in Kenya.
          </p>
        </div>

        <div class="benefits-grid">
          <div 
            v-for="(benefit, i) in benefits" 
            :key="i" 
            class="benefit-card"
            :class="{ 'card-hover': hoverIndex === i }"
            @mouseenter="hoverIndex = i"
            @mouseleave="hoverIndex = null"
          >
            <div class="card-inner">
              <div class="card-icon">
                <i :class="benefit.icon"></i>
              </div>
              <div class="card-content">
                <h3>{{ benefit.title }}</h3>
                <p>{{ benefit.desc }}</p>
              </div>
              <div class="card-border"></div>
            </div>
          </div>
        </div>

        <!-- Stats Banner -->
        <div class="stats-banner">
          <div class="stats-content">
            <h3>IpromiseAfrica Community in Numbers</h3>
            <p>Join our growing network of players, teams, and facility owners</p>
          </div>
          <div class="stats-grid">
            <div class="stat-item">
              <div v-if="pitchCount === null" class="stat-number stat-number--loading" aria-busy="true">…</div>
              <div
                v-else
                :key="'pitch-' + pitchCount"
                ref="pitchStatEl"
                class="stat-number stat-number--from-api"
                :data-count="pitchCount"
                data-suffix="+"
              >0+</div>
              <div class="stat-label">Sports Facilities</div>
            </div>
            <div class="stat-item">
              <div v-if="playerCount === null" class="stat-number stat-number--loading" aria-busy="true">…</div>
              <div
                v-else
                :key="'player-' + playerCount"
                ref="playerStatEl"
                class="stat-number stat-number--from-api"
                :data-count="playerCount"
                data-suffix="+"
              >0+</div>
              <div class="stat-label">Happy Players</div>
            </div>
            <div class="stat-item">
              <div class="stat-number stat-number--static" data-count="24" data-suffix="/7">0/7</div>
              <div class="stat-label">Support</div>
            </div>
            <div class="stat-item">
              <div class="stat-number stat-number--static" data-count="98" data-suffix="%">0%</div>
              <div class="stat-label">Satisfaction</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <MainFooter />
  </section>
</template>

<script>
/**
 * @fileoverview IpromiseAfrica facility booking landing (multi-sport hero, sport cards, benefits, stats).
 * @module routes/facility/BookFacility
 */
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import { buildApiUrl } from "@/config/api";

const FALLBACK_FACILITY_COUNT = 100;
const FALLBACK_PLAYER_COUNT = 10000;

const FACILITY_SPORTS = [
  {
    id: "football",
    name: "Football",
    icon: "fa-solid fa-futbol",
    image: require("@/assets/images/football_pitch/footballpitch.png"),
    typeFilter: "football",
    description: "Turf and grass pitches for 5-a-side, 7-a-side, and full-size matches.",
  },
  {
    id: "basketball",
    name: "Basketball",
    icon: "fa-solid fa-basketball",
    image: require("@/assets/images/football_pitch/basketball-court.jpg"),
    typeFilter: "basketball",
    description: "Indoor and outdoor courts for pick-up games, training, and leagues.",
  },
  {
    id: "volleyball",
    name: "Volleyball",
    icon: "fa-solid fa-volleyball",
    image: require("@/assets/images/football_pitch/volleyball-court.jpg"),
    typeFilter: "volleyball",
    description: "Courts for beach, indoor, and social volleyball sessions.",
  },
  {
    id: "cricket",
    name: "Cricket",
    icon: "fa-solid fa-baseball-bat-ball",
    image: require("@/assets/images/football_pitch/cricket-ground.jpg"),
    typeFilter: "cricket",
    description: "Grounds and nets for practice sessions, clubs, and weekend fixtures.",
  },
  {
    id: "tennis",
    name: "Tennis",
    icon: "fa-solid fa-table-tennis-paddle-ball",
    image: require("@/assets/images/football_pitch/tennis-court.jpg"),
    typeFilter: "tennis",
    description: "Hard and clay courts for singles, doubles, and coaching.",
  },
];

export default {
  name: "BookFacility",
  components: { MainNavbar, MainFooter },
  data() {
    return {
      heroImage: require("@/assets/images/football_pitch/facilities.png"),
      facilitySports: FACILITY_SPORTS,
      facilityCount: null,
      playerCount: null,
      hoverIndex: null,
      benefits: [
        {
          icon: "fa-solid fa-calendar-check",
          title: "Easy Booking System",
          desc: "Book football, basketball, volleyball, cricket, and tennis facilities in seconds.",
        },
        {
          icon: "fa-solid fa-map-location-dot",
          title: "Smart Location Finder (Feature Coming Soon)",
          desc: "Find venues near you with maps and detailed facility information on IpromiseAfrica.",
        },
        {
          icon: "fa-solid fa-money-bill-wave",
          title: "Flexible Payment (Feature Coming Soon)",
          desc: "Multiple payment options with secure transactions and instant receipts.",
        },
        {
          icon: "fa-solid fa-video",
          title: "Virtual Tours",
          desc: "Preview courts and pitches before you book — see exactly what you are getting.",
        },
      ],
    };
  },
  computed: {
    /** @deprecated alias for template refs during migration */
    pitchCount() {
      return this.facilityCount;
    },
  },
  mounted() {
    this.animateStaticStatNumbers();
    this.loadCommunityStats();
    this.addScrollEffects();
  },
  methods: {
    browseAllFacilities() {
      this.$router.push({ path: "/ipromiseafrica/facility/available" });
    },

    browseSport(sport) {
      const query = sport.typeFilter ? { type_filter: sport.typeFilter } : {};
      this.$router.push({ path: "/ipromiseafrica/facility/available", query });
    },

    scrollToContent() {
      document.getElementById("facility-sports")?.scrollIntoView({ behavior: "smooth" });
    },
    
    /**
     * Count-up animation for a single element (reads `data-count` and `data-suffix`).
     * @param {HTMLElement} el
     */
    runCountUpOnElement(el) {
      if (!el) return;
      const target = parseInt(el.getAttribute("data-count") || "0", 10);
      const suffix = el.getAttribute("data-suffix") || "+";
      if (!Number.isFinite(target) || target < 0) {
        el.textContent = `0${suffix}`;
        return;
      }
      if (target === 0) {
        el.textContent = `0${suffix}`;
        return;
      }
      const duration = 2000;
      const step = target / (duration / 16);
      let current = 0;
      const timer = setInterval(() => {
        current += step;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        el.textContent = `${Math.floor(current)}${suffix}`;
      }, 16);
    },

    animateStaticStatNumbers() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          this.runCountUpOnElement(el);
          observer.unobserve(el);
        });
      }, { threshold: 0.5 });
      this.$el.querySelectorAll(".stat-number--static").forEach((num) => observer.observe(num));
    },

    animateApiStatNumbers() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.runCountUpOnElement(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.5 });
      const pitchEl = this.$refs.pitchStatEl;
      const playerEl = this.$refs.playerStatEl;
      if (pitchEl) observer.observe(pitchEl);
      if (playerEl) observer.observe(playerEl);
    },

    async loadCommunityStats() {
      let facilities = FALLBACK_FACILITY_COUNT;
      let players = FALLBACK_PLAYER_COUNT;
      try {
        const [pitchRes, playerRes] = await Promise.all([
          fetch(buildApiUrl("/api/facilities/public/facility-count"), {
            headers: { Accept: "application/json" },
          }),
          fetch(buildApiUrl("/api/matches/public/player-count"), {
            headers: { Accept: "application/json" },
          }),
        ]);
        if (pitchRes.ok) {
          const body = await pitchRes.json();
          const n = Number(body?.count);
          if (Number.isFinite(n)) facilities = Math.max(0, Math.floor(n));
        }
        if (playerRes.ok) {
          const body = await playerRes.json();
          const n = Number(body?.count);
          if (Number.isFinite(n)) players = Math.max(0, Math.floor(n));
        }
      } catch {
        /* keep fallbacks */
      }
      this.facilityCount = facilities;
      this.playerCount = players;
      await this.$nextTick();
      this.animateApiStatNumbers();
    },
    
    addScrollEffects() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        });
      }, { threshold: 0.1 });
      
      this.$el.querySelectorAll(".benefit-card, .sport-card").forEach((card) => observer.observe(card));
    },
  }
};
</script>

<style scoped>
/* =============================
   Base Styles with CSS Variables
============================= */
.facility-home {
  min-height: 100vh;
  background: var(--background-color1);
  color: var(--text-color1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
}

/* =============================
   Navbar
============================= */
.navbar-fixed {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: rgba(14, 14, 14, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 255, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

[data-theme="dark"] .navbar-fixed {
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(0, 151, 0, 0.1);
}

/* =============================
   Hero Section
============================= */
.hero-section {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
}

.video-wrapper {
  width: 100%;
  max-width: 100vw;
  line-height: 0;
}

.hero-image {
  width: 100%;
  max-width: 100vw;
  height: auto;
  aspect-ratio: 2172 / 724;
  object-fit: contain;
  object-position: center;
  display: block;
}

.hero-container {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(2rem, 4vw, 2.5rem) clamp(1rem, 4vw, 2rem) clamp(1.25rem, 3vw, 2rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(1.5rem, 3vw, 2rem);
}

.hero-content {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.hero-footer {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding-top: clamp(0.75rem, 2vw, 1.25rem);
  border-top: 1px solid var(--border-color-hover1);
  display: flex;
  justify-content: center;
}

.tagline {
  margin-bottom: clamp(1rem, 2.5vw, 1.5rem);
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 2rem;
  background: rgba(0, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border: 1px solid var(--border-color-hover);
  color: var(--text-color1);
}

[data-theme="dark"] .tag {
  background: rgba(0, 151, 0, 0.1);
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

.tag i {
  font-size: 1rem;
  color: var(--text-color1);
}

.hero-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: var(--text-color1);
}

.highlight {
  color: var(--border-color-hover);
}

.hero-subtitle {
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: var(--text-color2);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto clamp(1.5rem, 3vw, 2rem);
  font-weight: 400;
}

/* Hero Actions */
.hero-actions {
  display: flex;
  gap: 18px;
  justify-content: center;
  margin-bottom: clamp(1.5rem, 3vw, 2rem);
  flex-wrap: wrap;
}

.hero-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 12px 30px;
  border-radius: 32px;
  font-size: clamp(0.95rem, 1.8vw, 1.1rem);
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s ease;
  outline: none;
  position: relative;
  overflow: hidden;
  border: none;
}

.hero-actions .btn.primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.25);
}

.hero-actions .btn.primary:hover {
  transform: translateY(-3px);
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.35);
}

.hero-actions .btn.outline {
  background: transparent;
  border: 2px solid var(--border-color-hover);
  color: var(--text-color1);
}

.hero-actions .btn.outline:hover {
  background: color-mix(in srgb, var(--border-color-hover) 12%, transparent);
  transform: translateY(-3px);
}

.btn-icon {
  transition: transform 0.3s ease;
  color: currentColor;
}

.hero-actions .btn.primary:hover .btn-icon {
  transform: translateX(5px);
}

/* Trust Indicators */
.trust-indicators {
  display: flex;
  justify-content: center;
  gap: clamp(0.75rem, 2vw, 1.5rem);
  margin-top: 0;
  flex-wrap: wrap;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: var(--background-color2);
  border-radius: 50px;
  border: 1px solid var(--border-color-hover);
}

.trust-icon {
  color: var(--border-color-hover);
  font-size: 1rem;
}

.trust-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color1);
}

/* Scroll Hint */
.scroll-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  padding: clamp(0.75rem, 2vw, 1rem) 0 0;
  cursor: pointer;
  animation: float 3s ease-in-out infinite;
}

.scroll-text {
  font-size: 0.8rem;
  color: var(--text-color2);
  margin-bottom: 0.5rem;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.scroll-line {
  width: 1px;
  height: 40px;
  background: var(--text-color2);
  opacity: 0.5;
  position: relative;
}

.scroll-dot {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: var(--border-color-hover);
  border-radius: 50%;
  animation: scrollDot 2s ease-in-out infinite;
}

/* =============================
   Section Wrappers
============================= */
.section-wrapper,
.benefits-wrapper {
  position: relative;
  z-index: 1;
}

.section-wrapper {
  padding: clamp(2.5rem, 5vw, 4rem) 0 clamp(3rem, 5vw, 4rem);
  background: var(--background-color1);
  scroll-margin-top: clamp(70px, 8vw, 90px);
}

/* Sports facility cards */
.sports-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.75rem;
}

.sport-card {
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  background: var(--background-color2);
  border: 1px solid rgba(0, 255, 255, 0.12);
  cursor: pointer;
  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.35s ease,
    border-color 0.35s ease;
}

[data-theme="dark"] .sport-card {
  border-color: rgba(0, 151, 0, 0.15);
}

.sport-card:hover {
  transform: translateY(-6px);
  border-color: var(--border-color-hover);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
}

.sport-card__media {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
}

.sport-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.sport-card:hover .sport-card__img {
  transform: scale(1.06);
}

.sport-card__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.65) 100%);
  pointer-events: none;
}

.sport-card__icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: var(--border-color-hover);
  font-size: 1.1rem;
  backdrop-filter: blur(6px);
}

.sport-card__body {
  padding: 1.35rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.65rem;
}

.sport-card__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-color1);
}

.sport-card__desc {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--text-color2);
  flex: 1;
}

.sport-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.35rem;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--border-color-hover);
  cursor: pointer;
  transition: gap 0.25s ease;
}

.sport-card:hover .sport-card__cta {
  gap: 0.75rem;
}

.benefits-wrapper {
  padding: clamp(3rem, 6vw, 5rem) 0 clamp(3.5rem, 6vw, 6rem);
  background: var(--background-color1);
  border-top: 1px solid var(--border-color-hover1);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Section Header */
.section-header {
  text-align: center;
  margin: 0 auto clamp(2.5rem, 5vw, 4rem);
  max-width: 720px;
  padding: 0 clamp(0.25rem, 2vw, 1rem);
}

.section-subtitle {
  display: block;
  color: var(--border-color-hover);
  font-size: clamp(0.78rem, 2vw, 0.9rem);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin: 0 0 0.75rem;
  line-height: 1.4;
}

.section-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  font-size: clamp(1.75rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 1rem;
  color: var(--text-color1);
  text-wrap: balance;
}

.section-title__line {
  display: block;
}

.section-description {
  color: var(--text-color2);
  font-size: clamp(0.95rem, 2.2vw, 1.1rem);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

/* =============================
   Benefits Grid
============================= */
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

.benefit-card {
  perspective: 1000px;
}

.card-inner {
  position: relative;
  background: var(--background-color2);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2.5rem 2rem;
  height: 100%;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

[data-theme="dark"] .card-inner {
  background: var(--background-color2);
  border: 1px solid rgba(0, 151, 0, 0.1);
}

.benefit-card:hover .card-inner {
  transform: translateY(-8px);
  border-color: var(--border-color-hover);
  box-shadow: 0 20px 40px rgba(0, 255, 255, 0.1);
}

[data-theme="dark"] .benefit-card:hover .card-inner {
  box-shadow: 0 20px 40px rgba(0, 151, 0, 0.1);
}

.card-icon {
  width: 60px;
  height: 60px;
  background: var(--gradient);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  color: var(--text-color1);
}

.card-content h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: var(--text-color1);
}

.card-content p {
  color: var(--text-color2);
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
}

.card-border {
  position: absolute;
  inset: 0;
  border-radius: 20px;
  padding: 2px;
  background: var(--gradient);
  -webkit-mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.benefit-card:hover .card-border {
  opacity: 1;
}

/* Stats Banner */
.stats-banner {
  background: var(--background-color3);
  border-radius: 24px;
  padding: 3rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 255, 0.1);
}

[data-theme="dark"] .stats-banner {
  background: var(--background-color3);
  border: 1px solid rgba(0, 151, 0, 0.1);
}

.stats-content {
  text-align: center;
  margin-bottom: 3rem;
}

.stats-content h3 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: var(--text-color1);
}

.stats-content p {
  color: var(--text-color2);
  max-width: 600px;
  margin: 0 auto;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
}

.stat-item {
  text-align: center;
  padding: 2rem;
  background: var(--background-color2);
  border-radius: 16px;
  border: 1px solid rgba(0, 255, 255, 0.1);
  transition: all 0.3s ease;
}

[data-theme="dark"] .stat-item {
  background: var(--background-color2);
  border: 1px solid rgba(0, 151, 0, 0.1);
}

.stat-item:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-4px);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--border-color-hover);
  margin-bottom: 0.5rem;
}

.stat-number--loading {
  opacity: 0.55;
  letter-spacing: 0.15em;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-label {
  font-size: 0.9rem;
  color: var(--text-color2);
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* =============================
   Animations
============================= */
@keyframes float {
  0%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(10px);
  }
}

@keyframes scrollDot {
  0% {
    transform: translateX(-50%) translateY(0);
    opacity: 0;
  }
  30% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    transform: translateX(-50%) translateY(30px);
    opacity: 0;
  }
}

/* =============================
   Responsive Design
============================= */

/* Tablet */
@media (max-width: 1024px) {
  .hero-title {
    font-size: clamp(2rem, 5vw, 3rem);
  }
  
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .sports-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero-container {
    padding: 1.75rem 1.25rem 1.25rem;
    gap: 1.25rem;
  }

  .hero-footer {
    padding-top: 0.65rem;
  }
  
  .hero-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .hero-actions .btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
  
  .trust-indicators {
    gap: 1rem;
  }
  
  .trust-item {
    padding: 0.5rem 1rem;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .sports-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-banner {
    padding: 2rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

/* Mobile */
@media (max-width: 480px) {
  .hero-title {
    font-size: 1.75rem;
  }
  
  .hero-subtitle {
    font-size: 0.95rem;
  }
  
  .tag {
    font-size: 0.8rem;
    padding: 0.6rem 1.5rem;
  }
  
  .benefits-wrapper {
    padding: 4rem 0;
  }
  
  .container {
    padding: 0 1rem;
  }
  
  .card-inner {
    padding: 2rem 1.5rem;
  }
  
  .section-header {
    margin-bottom: 2.25rem;
  }

  .section-title {
    font-size: 1.75rem;
  }
  
  .hero-actions .btn {
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
  }
}

/* Small Mobile */
@media (max-width: 360px) {
  .hero-title {
    font-size: 1.5rem;
  }
  
  .hero-actions {
    gap: 0.75rem;
  }
  
  .trust-indicators {
    flex-direction: column;
    align-items: center;
  }
  
  .benefit-card {
    min-width: 280px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .facility-home,
  .facility-home * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>